/**
 * In-memory ring buffer fed by CallDispatcher.onFrameObserved, mirrored
 * to the Dexie `eventLog` table for cross-reload persistence (FEAT-006).
 *
 *   onFrameObserved → append(entry)
 *      ├─ updates the in-memory entries[] (ring buffer, capped)
 *      └─ fire-and-forget eventLogRepo.append(...)   (lossless wire shape)
 *
 *   hydrate(stationId): loads the last DEFAULT_UI_EVENT_LOG_CAPACITY rows
 *   from Dexie when the Station boots, so F5 doesn't lose the trail.
 *   Called from useStationStore.init.
 *
 *   clear(stationId): wipes both layers in one go.
 */
import { create } from 'zustand';
import { db } from '@/persistence/dexie/db';
import { EventLogRepositoryDexie } from '@/persistence/dexie/repositories';
import type { StationId } from '@/domain/types';

export type MessageType = 'CALL' | 'CALLRESULT' | 'CALLERROR';

export interface UiEventLogEntry {
  readonly seq: number;
  readonly direction: 'in' | 'out';
  readonly messageType: MessageType;
  readonly messageId: string;
  /** Wire action name. null for CALLRESULT / CALLERROR (they pair by messageId). */
  readonly action: string | null;
  readonly payload: unknown;
  readonly at: number;
}

export interface EventLogStoreState {
  readonly entries: readonly UiEventLogEntry[];
  readonly capacity: number;
}

export interface EventLogStoreActions {
  /** Append + mirror to Dexie (fire-and-forget). */
  append(entry: Omit<UiEventLogEntry, 'seq'>): void;
  /** Load the last `capacity` rows for `stationId` into the ring buffer. */
  hydrate(stationId: StationId): Promise<void>;
  /** Wipe in-memory + Dexie for the given station. */
  clear(stationId: StationId | null): Promise<void>;
}

export type EventLogStore = EventLogStoreState & EventLogStoreActions;

export const DEFAULT_UI_EVENT_LOG_CAPACITY = 1_000;

const repo = new EventLogRepositoryDexie(db);
/** Tracks the active station so `append` can route to Dexie without prop-drilling. */
let currentStationId: StationId | null = null;

/** Test seam — Vitest swaps in null to disable Dexie mirroring. */
export function _setEventLogStationContext(stationId: StationId | null): void {
  currentStationId = stationId;
}

export const useEventLogStore = create<EventLogStore>((set, get) => ({
  entries: [],
  capacity: DEFAULT_UI_EVENT_LOG_CAPACITY,

  append(entry): void {
    const cap = get().capacity;
    const seq = (get().entries.at(-1)?.seq ?? 0) + 1;
    const ui: UiEventLogEntry = { ...entry, seq };
    const next: readonly UiEventLogEntry[] = [...get().entries, ui];
    set({ entries: next.length > cap ? next.slice(next.length - cap) : next });
    if (currentStationId !== null) {
      // Fire-and-forget — a Dexie hiccup must not block the UI tick.
      void repo
        .append({
          stationId: currentStationId,
          seq,
          action: entry.action,
          direction: entry.direction,
          messageType: entry.messageType,
          messageId: entry.messageId,
          payload: entry.payload,
          createdAt: entry.at,
        })
        .catch(() => {
          /* silent — the UI ring buffer is the source of truth for this tab */
        });
    }
  },

  async hydrate(stationId: StationId): Promise<void> {
    currentStationId = stationId;
    const cap = get().capacity;
    const rows = await repo.list(stationId, { limit: cap });
    const entries: readonly UiEventLogEntry[] = rows.map((r) => ({
      seq: r.seq,
      direction: r.direction,
      messageType: r.messageType,
      messageId: r.messageId,
      action: r.action,
      payload: r.payload,
      at: r.createdAt,
    }));
    set({ entries });
  },

  async clear(stationId: StationId | null): Promise<void> {
    set({ entries: [] });
    if (stationId !== null) {
      await repo.clear(stationId);
    }
  },
}));
